<template>
  <transition name="modal-fade">
    <div v-if="show" class="tri-assistant-overlay" @click.self="closeModal">
      <div class="tri-assistant-popup">
        <!-- Header with AI Sparkle -->
        <div class="tri-header">
          <div class="header-left">
            <div class="ai-icon">
              <i class="el-icon-magic-stick"></i>
            </div>
            <h3 class="tri-title">Automated Triangulation Assistant</h3>
          </div>
          <button class="tri-close-btn" @click="closeModal" aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Body Content -->
        <div class="tri-body" ref="contentBody">
          <!-- Loading State -->
          <div v-if="loading" class="loading-container">
            <div class="ai-loader">
              <div class="pulse-ring"></div>
              <div class="pulse-ring delay-1"></div>
              <div class="pulse-ring delay-2"></div>
              <i class="el-icon-loading"></i>
            </div>
            <span class="loading-text">Synthesizing data sources and analyzing gaps...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-container">
            <img src="@/assets/icons/error-illustration.svg" alt="Error" class="error-img" v-if="false" />
            <p class="error-msg">{{ error }}</p>
            <button @click="generateAssistantResponse" class="retry-btn">
              <i class="el-icon-refresh"></i> Retry Analysis
            </button>
          </div>

          <!-- Assistant Content -->
          <div v-else class="assistant-content">
            <!-- Summary Card -->
            <div class="insight-card summary-card">
              <div class="card-tag">Insight Summary</div>
              <p class="main-sentence">{{ mainInsight }}</p>
            </div>

            <!-- Detailed Sections -->
            <div class="sections-wrapper">
              <!-- Source Methodology Breakdown -->
              <div class="detail-section">
                <div class="section-badge methodology">Gap Explanation</div>
                <div class="section-text" v-html="formattedGapExplanation"></div>
              </div>

              <!-- Historical Trend Alignment -->
              <div class="detail-section">
                <div class="section-badge alignment">Historical Alignment</div>
                <div class="section-text" v-html="formattedTrendAlignment"></div>
              </div>

              <!-- Recommendation/Context -->
              <div class="detail-section recommendation">
                <div class="section-badge context">Assistant Context</div>
                <div class="section-text" v-html="formattedAssistantContext"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="tri-footer">
          <div class="footer-note">
            <i class="el-icon-info"></i>
            <span>This is an AI assistant providing context based on platform metadata.</span>
          </div>
          <button @click="closeModal" class="confirm-btn">Got it</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
// Known source methodology types — used for generating contextual gap explanations
const SOURCE_TYPES = {
  NDHS: { type: 'Survey-based', description: 'household surveys conducted periodically (every ~5 years)', method: 'population sampling' },
  MICS: { type: 'Survey-based', description: 'multi-indicator cluster household surveys', method: 'cluster sampling' },
  NHMIS: { type: 'Facility-based', description: 'routine reporting from health facilities', method: 'administrative records' },
  DHS: { type: 'Survey-based', description: 'household-based demographic health surveys', method: 'population sampling' },
  WHO: { type: 'Modelled estimates', description: 'modelled global estimates using multiple data sources', method: 'statistical modelling' },
  IHME: { type: 'Modelled estimates', description: 'modelled global estimates with uncertainty intervals', method: 'Bayesian meta-regression' },
  UNICEF: { type: 'Global estimate', description: 'international estimates compiled from national surveys', method: 'global aggregation' },
  NPC: { type: 'Census-based', description: 'national population census data', method: 'complete enumeration' },
  NNHS: { type: 'Survey-based', description: 'Nigeria national nutrition and health surveys', method: 'household sampling' },
  NHA: { type: 'Administrative', description: 'national health accounts tracking health expenditure', method: 'financial records' },
};

function getSourceType(sourceName) {
  if (!sourceName) return null;
  const upper = sourceName.toUpperCase();
  const match = Object.keys(SOURCE_TYPES).find((key) => upper.includes(key));
  return match ? { key: match, ...SOURCE_TYPES[match] } : null;
}

export default {
  name: 'TriangulationAssistant',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    triangulationData: {
      type: Array,
      default: () => [],
    },
    controlValues: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      response: null,
      error: null,
      parsedData: null,
    };
  },
  computed: {
    mainInsight() {
      return this.parsedData?.summary || 'Analyzing consistency between data sources...';
    },
    formattedGapExplanation() {
      return this.formatContent(this.parsedData?.gapExplanation);
    },
    formattedTrendAlignment() {
      return this.formatContent(this.parsedData?.trendAlignment);
    },
    formattedAssistantContext() {
      return this.formatContent(this.parsedData?.context);
    },
  },
  watch: {
    show(val) {
      if (val) {
        document.body.style.overflow = 'hidden';
        if (!this.response) {
          this.generateAssistantResponse();
        }
      } else {
        document.body.style.overflow = '';
      }
    },
    controlValues: {
      handler() {
        this.response = null;
        this.parsedData = null;
        this.error = null;
      },
      deep: true,
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    formatContent(text) {
      if (!text) return '';
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
    },

    // ─── Local Intelligence Engine ─────────────────────────────────────────
    async generateAssistantResponse() {
      this.loading = true;
      this.error = null;

      try {
        // Small delay for UX — gives the loader a chance to animate
        await new Promise((r) => setTimeout(r, 800));

        const primaryName = this.controlValues.primaryDataSource?.datasource
          // eslint-disable-next-line camelcase
          || this.controlValues.primaryDataSource?.full_name || 'Primary Source';
        const compareSources = (this.controlValues.dataSourcesCompare || [])
          // eslint-disable-next-line camelcase
          .map((ds) => ds.datasource || ds.full_name).filter(Boolean);
        // eslint-disable-next-line camelcase
        const indicatorName = this.controlValues.selectedIndicator?.full_name || 'this indicator';
        const locationName = this.controlValues.selectedLocation?.name || 'Nigeria';

        const primaryType = getSourceType(primaryName);
        const compareTypes = compareSources.map((n) => ({ name: n, info: getSourceType(n) }));

        // ── 1. SUMMARY ──────────────────────────────────────────────────────
        const allNames = [primaryName, ...compareSources].join(' and ');
        const summary = `The triangulation compares **${allNames}** for **${indicatorName}** in **${locationName}**, revealing important methodological differences that explain observed data gaps.`;

        // ── 2. GAP EXPLANATION ───────────────────────────────────────────────
        const gapLines = [];

        if (primaryType && compareTypes.length > 0) {
          const primaryDesc = `**${primaryName}** uses ${primaryType.type} data — ${primaryType.description} collected through ${primaryType.method}.`;
          gapLines.push(primaryDesc);

          compareTypes.forEach(({ name, info }) => {
            if (info) {
              gapLines.push(`**${name}** uses ${info.type} data — ${info.description} collected via ${info.method}.`);
            } else {
              gapLines.push(`**${name}** collects data through standard reporting procedures.`);
            }
          });

          // Detect facility vs survey gap
          const hasFacility = [primaryType, ...compareTypes.map((c) => c.info)].some((t) => t?.type === 'Facility-based');
          const hasSurvey = [primaryType, ...compareTypes.map((c) => c.info)].some((t) => t?.type === 'Survey-based');

          if (hasFacility && hasSurvey) {
            gapLines.push('\nFacility-based sources **typically undercount** actual population coverage because they only capture people who visit registered health facilities. Survey-based sources capture self-reported data from households, including those who use private or informal providers — which facility records completely miss.\n\nThis methodological gap is the primary driver of differences observed in the triangulation results.');
          } else if (compareTypes.some((c) => c.info?.type === 'Modelled estimates')) {
            gapLines.push('\nModelled estimates apply statistical adjustments for reporting gaps, population dynamics, and data completeness — which is why they often differ from raw survey or facility figures.');
          }
        } else {
          gapLines.push(`Data differences between ${allNames} for **${indicatorName}** are typically driven by differences in data collection methodology, recall periods, population coverage, and reporting completeness.`);
        }

        const gapExplanation = gapLines.join('\n');

        // ── 3. TREND ALIGNMENT ────────────────────────────────────────────────
        const trendLines = [];

        // Extract scores from triangulation data
        let consistencyScore = null;
        let complementarityScore = null;
        let bestSource = null;

        if (this.triangulationData && this.triangulationData.length > 0) {
          let highestScore = -1;

          this.triangulationData.forEach((entry) => {
            const sourceName = Object.keys(entry)[0];
            const sourceData = entry[sourceName];
            if (sourceData?.consistency?.score) {
              const score = parseFloat(sourceData.consistency.score);
              if (!consistencyScore) consistencyScore = score;
              if (score > highestScore) {
                highestScore = score;
                bestSource = sourceName;
              }
            }
            if (sourceData?.complementarity?.score && !complementarityScore) {
              complementarityScore = parseFloat(sourceData.complementarity.score);
            }
          });
        }

        if (consistencyScore !== null) {
          let level;
          if (consistencyScore >= 70) {
            level = 'strong';
          } else if (consistencyScore >= 40) {
            level = 'moderate';
          } else {
            level = 'weak';
          }
          trendLines.push(`The overall **consistency score** across sources is **${consistencyScore.toFixed(1)}%**, indicating **${level} alignment** between the compared data sources.`);
        }

        if (bestSource) {
          trendLines.push(`\n**${bestSource}** shows the highest consistency with peer sources and is likely the most stable reference for tracking historical trends of **${indicatorName}**.`);
        }

        // Recommend based on methodology
        if (primaryType?.type === 'Survey-based') {
          trendLines.push(`\nFor **long-term trend analysis**, survey-based sources like **${primaryName}** are generally preferred for ${indicatorName} because they capture population-level changes more comprehensively than administrative records.`);
        } else if (primaryType?.type === 'Facility-based') {
          trendLines.push(`\nFor **routine monitoring and short-term trends**, facility-based sources like **${primaryName}** offer more frequent updates. However, for benchmarking absolute coverage levels, cross-reference with survey data is recommended.`);
        }

        const trendAlignment = trendLines.join('\n') || 'Trend analysis is based on the consistency scores returned by the triangulation engine.';

        // ── 4. CONTEXT ────────────────────────────────────────────────────────
        const contextLines = [
          'As an analytical assistant, this analysis does **not** generate or suggest replacement values for any data source.',
          `\nThe differences observed between ${allNames} are expected given their distinct methodologies. Analysts should:`,
          '\n• Use **survey sources** (NDHS, MICS) for population-level coverage benchmarks',
          '• Use **facility sources** (NHMIS) for monitoring utilization trends within the health system',
          '• Use **modelled estimates** (WHO, IHME) for inter-country comparisons with uncertainty ranges',
          '• Document the rationale for source selection in any official reporting',
        ];

        if (complementarityScore !== null) {
          contextLines.push(`\nThe **complementarity score of ${complementarityScore.toFixed(1)}%** suggests that the sources capture ${complementarityScore >= 60 ? 'complementary, non-overlapping' : 'partially overlapping'} aspects of the health situation, reinforcing the value of multi-source triangulation.`);
        }

        const context = contextLines.join('\n');

        this.response = true;
        this.parsedData = {
          summary,
          gapExplanation,
          trendAlignment,
          context,
        };
      } catch (err) {
        console.error('Triangulation Assistant Error:', err);
        this.error = `Analysis generation failed: ${err.message}. Please retry.`;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },
};
</script>

<style lang="scss" scoped>
.tri-assistant-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background-color: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000 !important;
  padding: 1.5rem;
}

.tri-assistant-popup {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 650px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(52, 132, 129, 0.2);
}

.tri-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(90deg, #F0F8F7 0%, #FFFFFF 100%);
  border-bottom: 1px solid #E0EAE9;

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .ai-icon {
    width: 36px;
    height: 36px;
    background: #348481;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    box-shadow: 0 4px 10px rgba(52, 132, 129, 0.3);
  }

  .tri-title {
    margin: 0;
    font-family: 'DM Sans', sans-serif;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1A1A1A;
    letter-spacing: -0.2px;
  }
}

.tri-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  transition: all 0.2s;
  &:hover {
    color: #333;
    transform: rotate(90deg);
  }
}

.tri-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  background: #FAFBFA;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 0;
  gap: 2rem;

  .ai-loader {
    position: relative;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #348481;
    font-size: 1.5rem;

    .pulse-ring {
      position: absolute;
      border: 2px solid #348481;
      border-radius: 50%;
      width: 100%;
      height: 100%;
      animation: ripple 1.8s ease-out infinite;
      opacity: 0;
    }

    .delay-1 { animation-delay: 0.6s; }
    .delay-2 { animation-delay: 1.2s; }
  }

  .loading-text {
    color: #555;
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    font-weight: 500;
    max-width: 250px;
    text-align: center;
    line-height: 1.5;
  }
}

@keyframes ripple {
  0% { transform: scale(1); opacity: 0.8; }
  100% { transform: scale(2.5); opacity: 0; }
}

.assistant-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.insight-card {
  padding: 1.25rem;
  border-radius: 12px;
  position: relative;
}

.summary-card {
  background: #EBF7F6;
  border-left: 4px solid #348481;

  .card-tag {
    font-size: 0.7rem;
    text-transform: uppercase;
    font-weight: 700;
    color: #348481;
    margin-bottom: 0.5rem;
    letter-spacing: 0.5px;
  }

  .main-sentence {
    font-family: 'Inter', sans-serif;
    font-size: 1.2rem;
    font-weight: 700;
    color: #1A1A1A;
    line-height: 1.4;
    margin: 0;
  }
}

.detail-section {
  background: white;
  border: 1px solid #EAEAEA;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;

  .section-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 0.75rem;

    &.methodology { background: #FFF4E5; color: #B45309; }
    &.alignment { background: #EEF2FF; color: #4338CA; }
    &.context { background: #ECFDF5; color: #065F46; }
  }

  .section-text {
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    line-height: 1.6;
    color: #4B5563;
  }

  &.recommendation {
    background: linear-gradient(135deg, #F9FAFB 0%, #FFFFFF 100%);
    border: 1px dashed #348481;
  }
}

.tri-footer {
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #E0EAE9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;

  .footer-note {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6B7280;
    font-size: 0.85rem;
    i { color: #348481; }
  }
}

.confirm-btn {
  background: #348481;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #2a6b69;
    box-shadow: 0 4px 12px rgba(52, 132, 129, 0.2);
  }
}

.retry-btn {
  background: white;
  border: 1px solid #DC2626;
  color: #DC2626;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #FEF2F2;
  }
}

.error-container {
  padding: 3rem 0;
  text-align: center;
  .error-msg {
    color: #333;
    font-weight: 500;
    margin-bottom: 1.5rem;
  }
}

/* Animations */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter .tri-assistant-popup {
  transform: translateY(20px) scale(0.98);
}
.modal-fade-enter-active .tri-assistant-popup {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>
